import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getAccountVendors,
  getAccountRecurringExpenseById,
  createAccountRecurringExpense,
  updateAccountRecurringExpense
} from "../core/_requests";
import SearchableSelect from '../../../shared/components/SearchableSelect'

export default function AccountsNewRecurringExpense() {
  const navigate = useNavigate();
  const { id } = useParams();
  const expenseId = id ? Number(id) : null;
  const isEditing = expenseId !== null && !Number.isNaN(expenseId);

  const [saving, setSaving] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(false);

  // Form State
  const [profileName, setProfileName] = useState("");
  const [frequency, setFrequency] = useState("Monthly");
  const [account, setAccount] = useState("");
  const [startDate, setStartDate] = useState(new Date().toISOString().split("T")[0]);
  const [endDate, setEndDate] = useState("");
  const [amount, setAmount] = useState("");
  const [paidThrough, setPaidThrough] = useState("Petty Cash");
  const [vendorId, setVendorId] = useState("");
  const [status, setStatus] = useState("Active");

  // Dropdown options data
  const [vendors, setVendors] = useState<any[]>([]);

  useEffect(() => {
    // Load vendors
    getAccountVendors()
      .then((data) => {
        setVendors(data || []);
      })
      .catch((err) => console.error("Failed to load vendors:", err));
  }, []);

  useEffect(() => {
    if (!isEditing || expenseId == null) return;

    setLoadingInitial(true);
    getAccountRecurringExpenseById(expenseId)
      .then((data: any) => {
        if (data) {
          setProfileName(data.profileName || "");
          setFrequency(data.frequency || "Monthly");
          setAccount(data.account || "");
          setStartDate(data.startDate || new Date().toISOString().split("T")[0]);
          setEndDate(data.endDate || "");
          setAmount(data.amount !== undefined ? String(data.amount) : "");
          setPaidThrough(data.paidThrough || "Petty Cash");
          setVendorId(data.vendorId ? String(data.vendorId) : "");
          setStatus(data.status || "Active");
        }
      })
      .catch((err) => console.error("Failed to load recurring expense:", err))
      .finally(() => setLoadingInitial(false));
  }, [isEditing, expenseId]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!profileName || !frequency || !account || !startDate || !amount || !paidThrough) {
      alert("Please fill in all required fields.");
      return;
    }

    setSaving(true);
    const selectedVendor = vendors.find((v) => v.id === Number(vendorId));

    const payload = {
      profileName,
      frequency,
      account,
      startDate,
      endDate: endDate || null,
      nextExpenseDate: startDate, // Start date represents the first occurrence
      amount: Number(amount),
      paidThrough,
      vendorId: vendorId ? Number(vendorId) : null,
      vendorName: selectedVendor ? selectedVendor.displayName : null,
      status
    };

    try {
      if (isEditing && expenseId) {
        await updateAccountRecurringExpense(expenseId, payload);
      } else {
        await createAccountRecurringExpense(payload);
      }
      navigate("/accounts/purchase/recurring-expenses");
    } catch (err: any) {
      console.error("Save failed:", err);
      alert("Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    "w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all";

  if (loadingInitial) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-slate-50">
        <p className="text-slate-500 font-medium">Loading profile...</p>
      </div>
    );
  }

  return (
    <>
      <div className="flex h-screen w-full overflow-hidden bg-slate-50">
        
        <div id="sidebar-overlay" className="fixed inset-0 bg-black/50 z-40 hidden md:hidden"></div>
        <main className="flex-1 flex flex-col h-full overflow-hidden relative">
          
          <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6" id="main-scroll">
            <div className="max-w-5xl mx-auto pb-20">
              <div className="mb-6">
                <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                  Accounts / Purchase / Recurring Expenses
                </div>
                <h1 className="text-2xl font-bold text-slate-900 leading-tight">
                  {isEditing ? "Edit Recurring Expense" : "New Recurring Expense"}
                </h1>
                <p className="text-sm text-slate-500">
                  {isEditing ? "Edit recurring expense profile" : "Create a recurring expense profile"}
                </p>
              </div>

              <form onSubmit={handleSave} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
                <div className="mb-5">
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                    Profile Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={profileName}
                    onChange={(e) => setProfileName(e.target.value)}
                    required
                    placeholder="e.g. Office Rent Subscription"
                    className={inputClass}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                      Repeat Every <span className="text-red-500">*</span>
                    </label>
                    <SearchableSelect
                      value={frequency}
                      onChange={(e) => setFrequency(e.target.value)}
                      required
                      className={inputClass}
                    >
                      <option value="Weekly">Weekly</option>
                      <option value="Monthly">Monthly</option>
                      <option value="Yearly">Yearly</option>
                    </SearchableSelect>
                  </div>
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                      Expense Account <span className="text-red-500">*</span>
                    </label>
                    <SearchableSelect
                      value={account}
                      onChange={(e) => setAccount(e.target.value)}
                      required
                      className={inputClass}
                    >
                      <option value="">Select an account</option>
                      <option value="Advertising And Marketing">Advertising And Marketing</option>
                      <option value="Automobile Expense">Automobile Expense</option>
                      <option value="IT and Internet Expenses">IT and Internet Expenses</option>
                      <option value="Lodging">Lodging</option>
                      <option value="Meals and Entertainment">Meals and Entertainment</option>
                      <option value="Office Supplies">Office Supplies</option>
                      <option value="Travel Expense">Travel Expense</option>
                      <option value="Other Expenses">Other Expenses</option>
                    </SearchableSelect>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                      Start Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      required
                      className={inputClass}
                    />
                  </div>
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                      Amount <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      required
                      className={inputClass}
                    />
                  </div>
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                      Paid Through <span className="text-red-500">*</span>
                    </label>
                    <SearchableSelect
                      value={paidThrough}
                      onChange={(e) => setPaidThrough(e.target.value)}
                      required
                      className={inputClass}
                    >
                      <option value="Petty Cash">Petty Cash</option>
                      <option value="Undeposited Funds">Undeposited Funds</option>
                      <option value="Bank Account">Bank Account</option>
                      <option value="Credit Card">Credit Card</option>
                    </SearchableSelect>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                      Vendor
                    </label>
                    <SearchableSelect
                      value={vendorId}
                      onChange={(e) => setVendorId(e.target.value)}
                      className={inputClass}
                    >
                      <option value="">Select a vendor...</option>
                      {vendors.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.displayName}
                        </option>
                      ))}
                    </SearchableSelect>
                  </div>
                  {isEditing && (
                    <div className="mb-5">
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                        Status
                      </label>
                      <SearchableSelect
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        className={inputClass}
                      >
                        <option value="Active">Active</option>
                        <option value="Stopped">Stopped</option>
                        <option value="Expired">Expired</option>
                      </SearchableSelect>
                    </div>
                  )}
                </div>

                <div className="pt-6 mt-6 border-t border-slate-100 flex items-center gap-3">
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold shadow-sm hover:shadow transition-all disabled:opacity-50"
                  >
                    {saving ? "Saving..." : "Save Profile"}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="px-6 py-2.5 text-slate-500 hover:text-slate-700 text-sm font-bold transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
            
          </div>
        </main>
      </div>
      
    </>
  );
}

